import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AnalyticsManager from 'app/analytics';
import { getMediaPath, createGalleryItem } from 'app/functions';
import { createInsuranceLead, requestAccessToken } from 'redux/actions';
import { AnalyticsParamValues } from 'constants/analytics';
import * as routes from 'constants/routes';
import { HOMEPAGE_NAV_ITEM_BUTTON } from 'constants/homepage-types';
import SliderHeader from '../slider-header';
import SliderContent from '../slider-content';
import InsuranceItem from '../../../shared/insurance_item';
import expertIconPath from 'assets/icons/category-slide/action-buttons/ask-expert.svg';
import magazineIconPath from 'assets/icons/category-slide/action-buttons/tips.svg';
import './index.scss';

const BenefitsSlider = ({ data, creativeSlot, promotionName }) => {
    const dispatch = useDispatch();

    const userData = useSelector((store) => store.config.UserData);
    const insuranceData = useSelector((store) => store.config.insuranceData);
    const translations = useSelector((store) => store.config.Translations);
    const categoriesMap = useSelector((store) => store.config.categoriesMap); 

    const [headerButtons, setHeaderButtons] = useState([]);
    const [contentItems, setContentItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const sliderId = data['id'];
    const sliderTitle = data['title'];
    const sliderIconPath = getMediaPath(data['icon_path']);
    const sliderItems = data['items'];
    const sliderAnalyticsType = AnalyticsParamValues.INSURANCE_ITEMS_SLIDER;

    const expertId = Number(data['related_expert_id']);
    const magazineId = Number(data['related_magazine_id']);
    const magazineName = data['related_magazine'];

    const redirectToInsuranceLandingPage = () => {
        const insuranceUrl = insuranceData?.link;

        AnalyticsManager.logInsuranceSliderPress();

        if (userData) {
            setIsLoading(true);

            dispatch(requestAccessToken(
                (response) => {
                    window.open(`${insuranceUrl}?accessToken=${response}`, '_blank').focus();
                    setIsLoading(false);
                },
                () => setIsLoading(false),
            ));
        } else {
            window.open(insuranceUrl, '_blank').focus();
        }
    };


    useEffect(() => {
        let itemsForLog = [];
        data.items.forEach((bannerData, index) => {
            let category = categoriesMap?.[String(bannerData?.url_object?.parameters?.id)] || null;
            const item = createGalleryItem({ 
                link: bannerData?.url_object,  
                index, 
                category: category, 
                benefit: bannerData?.benefit 
            });
            itemsForLog.push(item);
        });
    
        AnalyticsManager.logViewPromotion(
            itemsForLog,
            creativeSlot,
            promotionName
        );
    }, []);


    const logSelectPromotion = (item, index) => {
        let category = categoriesMap?.[String(item?.url_object?.parameters?.id)] || null;
        const itemForLog = createGalleryItem({ 
            link: item?.url_object, 
            index, 
            category: category, 
            benefit: item?.benefit 
        });
        AnalyticsManager.logSelectPromotion(
            itemForLog,
            creativeSlot,
            promotionName
        );
    }

    const redirectToInsuranceLead = (insuranceType) => {
        setIsLoading(true);

        AnalyticsManager.logInsuranceSliderItemPress(insuranceType);

        dispatch(createInsuranceLead(
            { insurance_type: insuranceType },
            (response) => {
                window.open(response, '_blank').focus();
                setIsLoading(false);
            },
            () => setIsLoading(false),
        ));
    };

    const getHeaderButtons = () => {
        const buttonsList = [];

        if (expertId) {
            buttonsList.push({
                key: 'expert-button',
                text: translations['category_expert_button'],
                href: `${routes.experts}?id=${expertId}`,
                iconPath: expertIconPath,
                iconModifier: 'expert-icon',
                iconDir: 'right',
                type: HOMEPAGE_NAV_ITEM_BUTTON,
            });
        }

        if (magazineId) {
            buttonsList.push({
                key: 'magazine-button',
                text: `${translations['category_magazine_button']} ${magazineName}`,
                href: `${routes.magazine}/${magazineName}`,
                iconPath: magazineIconPath,
                iconModifier: 'magazine-icon',
                iconDir: 'right',
                type: HOMEPAGE_NAV_ITEM_BUTTON,
            });
        }

        return buttonsList;
    };

    const getContentItems = () => sliderItems.map(
        (itemData, index) => {
            const insuranceType = Number(itemData['insurance_type']);

            const handleItemClick = () => {
                logSelectPromotion(itemData, index);
                if (insuranceType > 0) {
                    redirectToInsuranceLead(insuranceType);
                }
            };

            return (
                <InsuranceItem data={itemData} onClick={handleItemClick} />
            );
        }
    );

    useEffect(() => {
        setHeaderButtons(getHeaderButtons);
        setContentItems(getContentItems);
    }, [data]);

    return (
        <div className="homepage-insurance-slider">
            <SliderHeader
                id={sliderId}
                title={sliderTitle}
                iconPath={sliderIconPath}
                onTitleClick={redirectToInsuranceLandingPage}
                tabsList={[]}
                buttonsList={headerButtons}
                isLoading={isLoading}
                analyticsType={sliderAnalyticsType}
            />

            <SliderContent
                itemsList={contentItems}
                isLoading={isLoading}
                creativeSlot={creativeSlot}
            />
        </div>
    );
};

export default BenefitsSlider;
